import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type { Deployment, DeploymentListResponse } from '../../../types/backend';

export interface ServiceComponentSummary {
  id: string;
  service: string;
  portfolio: string;
  portfolio_id?: string | null;
  sc_portfolio_ids?: Record<string, string>;
  sc_product_ids?: Record<string, string>;
  name: string;
  description: string;
  scope: string;
  current_version: string | null;
  auto_deploy: boolean;
  provisioning_type: string | null;
  source_repository: string | null;
  depends_on: string[];
  status: string;
}

export interface ServiceComponentDetail extends ServiceComponentSummary {
  depends_on: string[];
  versions: Array<{
    name: string;
    sc_artifact_ids: Record<string, string>;
    created_time: string;
    active: boolean;
  }>;
  source: string;
  created_at: string;
  updated_at: string;
  order?: number;
}

export interface Portfolio {
  id: string;
  name: string;
  display_name: string;
  description: string;
  sc_portfolio_id?: string | null;
  regional_portfolio_ids?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface DependencyNode {
  product_id: string;
  name: string;
  service: string;
  depends_on: string[];
}

export interface PortfolioShare {
  id: string;
  portfolio_id: string;
  customer_id: string;
  account_id: string;
  desired_regions: string[];
  desired_status: string;
  actual_regions_shared: string[];
  actual_regions_accepted: string[];
  actual_status: string;
  last_checked: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConvergenceResource {
  resource_id: string;
  status: string;
  updated_at: string | null;
  error: string | null;
}

export interface ConvergenceDomain {
  domain_type: string;
  total: number;
  converged: number;
  failed: number;
  in_progress: number;
  resources: ConvergenceResource[];
}

export interface PipelineCredentialResponse {
  client_id: string;
  service_id: string;
  customer_id: string;
  label: string;
  scopes: string[];
  status: 'active' | 'revoked';
  secret_arn: string;
  created_at: string;
  created_by: string;
  revoked_at: string | null;
  client_secret?: string;
  token_endpoint?: string;
}

export interface ConvergenceStatusResponse {
  customer_id: string;
  domains: ConvergenceDomain[];
}

export interface CatalogService {
  // Service components
  listServiceComponents(): Promise<{ products: ServiceComponentSummary[]; count: number }>;
  getServiceComponent(productId: string, service: string): Promise<ServiceComponentDetail>;
  createServiceComponent(params: {
    service: string;
    name: string;
    description?: string;
    scope?: string;
    depends_on?: string[];
    auto_deploy?: boolean;
    portfolio?: string;
    order?: number;
  }): Promise<ServiceComponentDetail>;
  updateServiceComponent(productId: string, service: string, updates: Record<string, unknown>): Promise<ServiceComponentDetail>;
  deleteServiceComponent(productId: string, service: string): Promise<{ deleted: boolean }>;
  listVersions(productId: string, service: string): Promise<{ versions: ServiceComponentDetail['versions'] }>;
  activateVersion(productId: string, service: string, versionName: string): Promise<{ success: boolean }>;
  deactivateVersion(productId: string, service: string, versionName: string): Promise<{ success: boolean }>;
  getServiceComponentSources(productName: string): Promise<{ product_name: string; sources: Record<string, 'literal' | 'resolver' | 'operator-input'> }>;
  getDependencyGraph(): Promise<{ nodes: DependencyNode[] }>;
  registerArtifact(params: {
    product_id: string;
    service: string;
    version_name: string;
    sc_artifact_ids: Record<string, string>;
  }): Promise<{ product_id: string; version_name: string; sc_artifact_ids: Record<string, string>; total_versions: number }>;

  // Services
  listServices(): Promise<{ services: string[] }>;
  getServiceComponents(service: string): Promise<{ service: string; products: ServiceComponentSummary[]; count: number }>;

  // Portfolios
  listPortfolios(): Promise<{ portfolios: Portfolio[] }>;
  getPortfolio(portfolioId: string): Promise<Portfolio>;
  createPortfolio(params: {
    name: string;
    display_name?: string;
    description?: string;
  }): Promise<Portfolio>;
  updatePortfolio(portfolioId: string, updates: Record<string, unknown>): Promise<Portfolio>;
  deletePortfolio(portfolioId: string): Promise<{ deleted: boolean }>;
  getPortfolioServiceComponents(portfolioId: string): Promise<{ products: ServiceComponentSummary[] }>;

  // Sharing
  previewSubscribe(params: {
    customer_id: string;
    account_id: string;
    services: string[];
    include_products?: string[];
  }): Promise<{
    customer_id: string;
    account_id: string;
    deployments_to_create: Array<{
      product_id: string;
      product_name: string;
      service: string;
      region: string;
      is_reactivation: boolean;
    }>;
    portfolios_to_share: Array<{ portfolio: string; regions: string[] }>;
    deployment_count: number;
    portfolio_count: number;
  }>;
  getSharingStatus(customerId: string, accountId?: string): Promise<{
    shares: PortfolioShare[];
    total_count: number;
  }>;

  // Convergence
  getConvergenceStatus(customerId: string): Promise<ConvergenceStatusResponse>;

  // Pipeline Credentials
  listPipelineCredentials(serviceId: string): Promise<{ credentials: PipelineCredentialResponse[]; total_count: number }>;
  listAllPipelineCredentials(): Promise<{ credentials: PipelineCredentialResponse[]; total_count: number }>;
  createPipelineCredential(params: { service_id: string; label: string }): Promise<PipelineCredentialResponse>;
  revokePipelineCredential(serviceId: string, clientId: string): Promise<{ revoked: boolean }>;

  // Deployments
  listDeployments(customerId: string): Promise<DeploymentListResponse>;
  approveDeployment(customerId: string, deploymentId: string): Promise<{ success: boolean }>;
  acceptActualState(customerId: string, deploymentId: string): Promise<{ success: boolean }>;
  acknowledgeDeployment(customerId: string, deploymentId: string): Promise<Deployment>;
  deleteDeployment(customerId: string, deploymentId: string): Promise<{ success: boolean }>;
  setDesiredState(customerId: string, deploymentId: string, state: Record<string, unknown>): Promise<{ success: boolean }>;
  getDeploymentHistory(customerId: string, deploymentId: string): Promise<{ history: import('../../../types/backend').DeploymentHistory[] }>;
  requestHeartbeat(customerId: string, accountId: string, region?: string): Promise<{ success: boolean }>;
  setDesiredParameters(customerId: string, deploymentId: string, parameters: Record<string, string | null>): Promise<import('../types').SetDesiredParametersResponse>;
  previewResolution(customerId: string, accountId: string, productId: string, service: string, deploymentId?: string): Promise<import('../types').PreviewResolutionResponse>;
  resolveRescue(customerId: string, deploymentId: string): Promise<Deployment>;
  triggerReconciler(domainType: string): Promise<{ success: boolean }>;
  subscribe(params: {
    customer_id: string;
    account_id: string;
    services: string[];
    regions?: string[];
    include_products?: string[];
  }): Promise<{ deployments: Deployment[] }>;
  unsubscribe(params: {
    customer_id: string;
    services: string[];
    account_id?: string;
    region?: string;
  }): Promise<{ success: boolean }>;
}

export const createCatalogService = (idToken: string): CatalogService => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    // Service components
    listServiceComponents: () =>
      rpcCall(client, 'catalog.ListServiceComponents'),

    getServiceComponent: (productId, service) =>
      rpcCall(client, 'catalog.GetServiceComponent', { product_id: productId, service }),

    createServiceComponent: (params) =>
      rpcCall(client, 'catalog.CreateServiceComponent', params as Record<string, unknown>),

    updateServiceComponent: (productId, service, updates) =>
      rpcCall(client, 'catalog.UpdateServiceComponent', { product_id: productId, service, ...updates }),

    deleteServiceComponent: (productId, service) =>
      rpcCall(client, 'catalog.DeleteServiceComponent', { product_id: productId, service }),

    listVersions: (productId, service) =>
      rpcCall(client, 'catalog.ListVersions', { product_id: productId, service }),

    activateVersion: (productId, service, versionName) =>
      rpcCall(client, 'catalog.ActivateVersion', { product_id: productId, service, version_name: versionName }),

    deactivateVersion: (productId, service, versionName) =>
      rpcCall(client, 'catalog.DeactivateVersion', { product_id: productId, service, version_name: versionName }),

    getServiceComponentSources: (productName) =>
      rpcCall(client, 'catalog.GetServiceComponentSources', { product_name: productName }),

    getDependencyGraph: () =>
      rpcCall(client, 'catalog.GetDependencyGraph'),

    registerArtifact: (params) =>
      rpcCall(client, 'service_components.RegisterArtifact', params as Record<string, unknown>),

    // Services
    listServices: () =>
      rpcCall(client, 'catalog.ListServices'),

    getServiceComponents: (service) =>
      rpcCall(client, 'catalog.GetServiceComponentsForService', { service }),

    // Portfolios
    listPortfolios: () =>
      rpcCall(client, 'catalog.ListPortfolios'),

    getPortfolio: (portfolioId) =>
      rpcCall(client, 'catalog.GetPortfolio', { portfolio_id: portfolioId }),

    createPortfolio: (params) =>
      rpcCall(client, 'catalog.CreatePortfolio', params as Record<string, unknown>),

    updatePortfolio: (portfolioId, updates) =>
      rpcCall(client, 'catalog.UpdatePortfolio', { portfolio_id: portfolioId, ...updates }),

    deletePortfolio: (portfolioId) =>
      rpcCall(client, 'catalog.DeletePortfolio', { portfolio_id: portfolioId }),

    getPortfolioServiceComponents: (portfolioId) =>
      rpcCall(client, 'catalog.GetPortfolioServiceComponents', { portfolio_id: portfolioId }),

    // Sharing
    previewSubscribe: (params) =>
      rpcCall(client, 'catalog.PreviewSubscribe', params as Record<string, unknown>),

    getSharingStatus: (customerId, accountId) =>
      rpcCall(client, 'catalog.GetSharingStatus', {
        customer_id: customerId,
        ...(accountId && { account_id: accountId }),
      }),

    // Convergence
    getConvergenceStatus: (customerId) =>
      rpcCall(client, 'catalog.GetConvergenceStatus', { customer_id: customerId }),

    // Pipeline Credentials
    listPipelineCredentials: (serviceId) =>
      rpcCall(client, 'catalog.ListPipelineCredentials', { service_id: serviceId }),

    listAllPipelineCredentials: () =>
      rpcCall(client, 'catalog.ListPipelineCredentials', {}),

    createPipelineCredential: (params) =>
      rpcCall(client, 'catalog.CreatePipelineCredential', params as Record<string, unknown>),

    revokePipelineCredential: (serviceId, clientId) =>
      rpcCall(client, 'catalog.RevokePipelineCredential', { service_id: serviceId, client_id: clientId }),

    // Deployments
    listDeployments: (customerId) =>
      rpcCall(client, 'deployments.ListDeployments', { customer_id: customerId }),

    approveDeployment: (customerId, deploymentId) =>
      rpcCall(client, 'deployments.ApproveDeployment', { customer_id: customerId, deployment_id: deploymentId }),

    acceptActualState: (customerId, deploymentId) =>
      rpcCall(client, 'deployments.AcceptActualState', { customer_id: customerId, deployment_id: deploymentId }),

    acknowledgeDeployment: (customerId, deploymentId) =>
      rpcCall(client, 'deployments.BulkAcknowledge', { customer_id: customerId, deployment_ids: [deploymentId], action: 'acknowledge' }),

    deleteDeployment: (customerId, deploymentId) =>
      rpcCall(client, 'deployments.DeleteDeployment', { customer_id: customerId, deployment_id: deploymentId }),

    setDesiredState: (customerId, deploymentId, state) =>
      rpcCall(client, 'deployments.SetDesiredState', { customer_id: customerId, deployment_id: deploymentId, ...state }),

    getDeploymentHistory: (customerId, deploymentId) =>
      rpcCall(client, 'deployments.GetHistory', { customer_id: customerId, deployment_id: deploymentId }),

    setDesiredParameters: (customerId: string, deploymentId: string, parameters: Record<string, string | null>) =>
      rpcCall(client, 'deployments.SetDesiredParameters', { customer_id: customerId, deployment_id: deploymentId, parameters }),

    previewResolution: (customerId: string, accountId: string, productId: string, service: string, deploymentId?: string) =>
      rpcCall(client, 'service_offerings.PreviewResolution', {
        customer_id: customerId,
        account_id: accountId,
        product_id: productId,
        service,
        ...(deploymentId && { deployment_id: deploymentId }),
      }),

    requestHeartbeat: (customerId, accountId, region) =>
      rpcCall(client, 'deployments.RequestHeartbeat', {
        customer_id: customerId,
        account_id: accountId,
        ...(region && { region }),
      }),

    resolveRescue: (customerId: string, deploymentId: string) =>
      rpcCall(client, 'deployments.ResolveRescue', {
        customer_id: customerId,
        deployment_id: deploymentId,
      }),

    triggerReconciler: (domainType: string) =>
      rpcCall(client, 'catalog.TriggerReconciler', {
        domain_type: domainType,
      }),

    subscribe: (params) =>
      rpcCall(client, 'deployments.Subscribe', params as Record<string, unknown>),

    unsubscribe: (params) =>
      rpcCall(client, 'deployments.Unsubscribe', params as Record<string, unknown>),
  };
};

export const useCatalogService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return createCatalogService(idToken);
  }, [idToken]);
};
